import React from 'react';
import { COLORS, SPACING, TYPOGRAPHY } from '../../config/branding';

/**
 * LoadingSpinner Component - Consistent loading indicator
 *
 * @param {string} size - Spinner size: 'sm', 'md', 'lg', 'xl'
 * @param {string} color - Spinner color (hex value)
 * @param {string} text - Optional loading text
 * @param {boolean} fullscreen - Display fullscreen overlay
 */
const LoadingSpinner = ({
  size = 'md',
  color = COLORS.primary,
  text = '',
  fullscreen = false,
}) => {
  const sizeMap = {
    sm: 16,
    md: 24,
    lg: 40,
    xl: 64,
  };

  const spinnerSize = sizeMap[size];

  const spinnerStyle = {
    width: `${spinnerSize}px`,
    height: `${spinnerSize}px`,
    animation: 'spin 1s linear infinite',
  };

  const containerStyle = fullscreen ? {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999,
  } : {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.md,
  };

  const textStyle = {
    marginTop: SPACING.md,
    fontSize: TYPOGRAPHY.fontSize.base,
    color: fullscreen ? COLORS.white : COLORS.text.primary,
    fontWeight: TYPOGRAPHY.fontWeight.medium,
  };

  return (
    <div style={containerStyle}>
      <svg
        style={spinnerStyle}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke={fullscreen ? COLORS.white : color}
          strokeWidth="3"
          strokeOpacity="0.25"
        />
        <path
          d="M12 2a10 10 0 0 1 10 10"
          stroke={fullscreen ? COLORS.white : color}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </svg>
      {text && <div style={textStyle}>{text}</div>}
    </div>
  );
};

/**
 * LoadingOverlay - Full page loading overlay
 */
export const LoadingOverlay = ({ text = 'Loading...' }) => {
  return <LoadingSpinner fullscreen={true} text={text} size="xl" />;
};

/**
 * InlineLoader - Small inline loading spinner
 */
export const InlineLoader = ({ text = '' }) => {
  return <LoadingSpinner size="sm" text={text} />;
};

export default LoadingSpinner;
